import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  cn,
  Iskelet,
  Kart,
  MetinAlani,
  Rozet,
  Secim,
  Tablo,
  FiltreKarti,
} from "@koclukpanel/ui";
import type { DersNetGirisi } from "@koclukpanel/contracts";
import { SayfaBasligiEki } from "./Kabuk";
import {
  ApiHatasi,
  api,
  denemeleriGetir,
  ogrenciListesi,
  topluNetGirisi,
  topluOnizle,
  topluUygula,
  uzmanSonucGirisi,
  type DenemeSatiri,
  type TopluOnizleme,
} from "./api";

/**
 * UZMAN TARAFI SONUÇ GİRİŞİ — F-EXM-04
 *
 * ================================================================
 * ŞARTNAME DÖRT YÖNTEM TANIMLIYOR, ARAYÜZDE BİRİ VARDI
 * ================================================================
 * Öğrenci kendi cevaplarını kodlayabiliyordu (öğrenci uygulaması).
 * Uzmanın üç yolu — cevap cevap giriş, ders bazlı net, toplu tablo —
 * sunucuda yazılıydı ve hiçbirine ulaşılamıyordu. Optik okuyucu
 * dosyası dördüncü yöntem; onun ucu henüz yok.
 *
 * ================================================================
 * ANAHTARI EKSİK DENEMEDE GİRİŞ AÇILMIYOR
 * ================================================================
 * Cevap anahtarı olmayan bir bölümün sonucu değerlendirilemez —
 * girilen cevaplar sessizce yanlış nete dönüşürdü. Liste bunu
 * satırında yazıyor ve giriş formunu açmıyor.
 *
 * ================================================================
 * TOPLU YÜKLEMEDE ÖNCE ÖN İZLEME
 * ================================================================
 * Sunucu ön izlemede HİÇBİR ŞEY YAZMIYOR. Ekran da doğrudan
 * uygulamaya izin vermiyor: yapıştırılan tablonun kaç satırı
 * eşleşti, kaçı hatalı ve kimin sonucu ÜZERİNE YAZILACAK —
 * bunlar görülmeden verilen onay, onay değil.
 */

type Yontem = "cevap" | "net" | "toplu";

const YONTEM_ADI: Record<Yontem, string> = {
  cevap: "Cevap cevap",
  net: "Ders bazlı net",
  toplu: "Toplu tablo",
};

/**
 * ================================================================
 * SEÇİMİN BEDELİ SEÇERKEN OKUNUYOR — F-EXM-04
 * ================================================================
 * Çizimin (`SonucGirisi.dc.html`) kendi cümlesi: *"Hangisini seçtiğin
 * NE KADAR ANALİZ ALACAĞINI belirliyor."* Şerit üç çıplak etiket
 * gösteriyordu; sonuç ise ancak "Ders bazlı net" seçildikten SONRA,
 * açılan kartın açıklamasında yazıyordu.
 *
 * Yani uzman neyi kaybettiğini kaybettikten sonra öğreniyordu. Aynı
 * gün `Denemeler` ekranında bunun sonucunu düzelttim: konu analizi
 * olmayan denemede telafi paneli "yanlış çıkmadı" diyordu. Sebep
 * burasıydı.
 *
 * ÜÇ METİN DE ÖLÇÜLEREK YAZILDI, tahminle değil:
 *   `cevap`  -> `sonucIsle(..., cevaplar)`  — soru/konu analizi var
 *   `toplu`  -> `topluUygula` da `sonucIsle`ye CEVAPLARI geçiriyor,
 *               yani o da tam analiz üretiyor (`deneme.service.ts`)
 *   `net`    -> `topluNetGirisi`; `rawAnswers` BOŞ kalıyor ve konu
 *               analizi HİÇ üretilmiyor — bilinçli bir karar
 */
const YONTEM_BEDELI: Record<Yontem, { metin: string; kayip: boolean }> = {
  cevap: { metin: "Soru ve konu bazlı analiz üretilir", kayip: false },
  toplu: { metin: "Cevapları taşır — soru ve konu analizi üretilir", kayip: false },
  net: { metin: "Konu analizi ÜRETİLMEZ", kayip: true },
};

const SECENEKLER = ["A", "B", "C", "D", "E"] as const;

export function SonucGirisi() {
  const [denemeId, setDenemeId] = useState("");
  const [yontem, setYontem] = useState<Yontem>("toplu");

  const denemeler = useQuery({
    queryKey: ["denemeler"],
    queryFn: denemeleriGetir,
    retry: false,
  });

  if (denemeler.isPending) return <Iskelet satir={4} />;

  if (denemeler.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Denemeler alınamadı"
        aciklama={denemeler.error instanceof ApiHatasi ? denemeler.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void denemeler.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (denemeler.data.length === 0) {
    return (
      <BosDurum
        baslik="Tanımlı deneme yok"
        aciklama="Önce “Deneme tanımla” ekranından deneme ve cevap anahtarı girilmeli; anahtarsız denemede sonuç değerlendirilemez."
      />
    );
  }

  const secili = denemeler.data.find((d) => d.id === denemeId) ?? null;
  /*
    ANAHTARI EKSİK BÖLÜM VARSA giriş yapılamaz. Sunucu da sonucu
    değerlendiremez; formu açmak, girilen cevapların sessizce yanlış
    nete dönüşmesi demek olurdu.
  */
  const anahtarEksik = secili ? secili.bolumler.some((b) => !b.anahtarTam) : false;

  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki aciklama="Deneme sonuçlarını öğrenci bazında girin; net ve konu analizi sistem tarafından üretilir." />
      {/* Deneme seçimi UNİKOÇ'taki gibi beyaz filtre kartında (tarama-1 §2.11). */}
      <FiltreKarti baslik="Deneme ve öğrenci">
        <Secim
          etiket="Deneme"
          value={denemeId}
          onChange={(e) => setDenemeId(e.target.value)}
          className="w-full sm:w-96"
        >
          <option value="">Seçin…</option>
          {denemeler.data.map((d) => (
            <option key={d.id} value={d.id}>
              {d.ad} — {new Date(d.denemeTarihi).toLocaleDateString("tr-TR")}
              {d.bolumler.some((b) => !b.anahtarTam) ? " · anahtar eksik" : ""}
            </option>
          ))}
        </Secim>
      </FiltreKarti>

      {secili === null ? (
        <BosDurum
          baslik="Deneme seçin"
          aciklama="Sonuç girmek istediğiniz denemeyi yukarıdan seçin."
        />
      ) : anahtarEksik ? (
        <Kart ton="uyari" baslik="Cevap anahtarı eksik">
          <p className="text-notr-800 text-sm">
            Bu denemenin şu bölümlerinde anahtar tamamlanmamış:{" "}
            <strong>
              {secili.bolumler
                .filter((b) => !b.anahtarTam)
                .map((b) => `${b.ad} (${b.anahtarSayisi}/${b.soruSayisi})`)
                .join(", ")}
            </strong>
            . Anahtar tamamlanmadan girilen cevaplar yanlış nete dönüşür; giriş açılmıyor. “Deneme
            tanımla” ekranından anahtarı tamamlayın.
          </p>
        </Kart>
      ) : (
        <>
          {/*
            ================================================================
            SEKME DEĞİL, BASILI KALAN DÜĞME
            ================================================================
            Burada `role="tab"` + `aria-selected` vardı. ARIA sekme deseni
            yalnızca o iki niteliği yazmakla kurulmuyor: ok tuşlarıyla
            gezinme, gezinen odak (`tabindex="-1"` + roving) ve her sekmenin
            bir `tabpanel`e bağlanması gerekiyor. Ölçüldü — bu dosyada
            `onKeyDown` de `tabIndex` de YOKTU.

            Yarısını yazmak hiç yazmamaktan KÖTÜ: ekran okuyucu kullanıcısına
            "burada ok tuşlarıyla gezebilirsin" diye söz verilir ve o söz
            tutulmaz. Gerekçe zaten `packages/ui/src/Sekmeler.tsx` içinde
            yazılı ve on bir ekran ona göre dönüştürülmüştü; dört şerit
            atlanmıştı, bu onlardan biri.

            `Sekmeler` bileşenine geçilmedi çünkü buradaki düğme iki satır
            taşıyor: yöntem adı ve BEDELİ. Bedeli kaldırmak, seçimin
            sonucunu seçimden sonra öğretmeye geri dönmek olurdu.
          */}
          <div role="group" aria-label="Giriş yöntemi" className="grid gap-2 sm:grid-cols-3">
            {(Object.keys(YONTEM_ADI) as Yontem[]).map((y) => {
              const bedel = YONTEM_BEDELI[y];
              const secili = yontem === y;
              return (
                <button
                  key={y}
                  type="button"
                  aria-pressed={secili}
                  onClick={() => setYontem(y)}
                  className={cn(
                    "min-h-dokunma rounded-alan flex flex-col items-start justify-center px-4 py-2 text-left",
                    "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                    secili
                      ? "bg-marka-600 text-notr-0"
                      : "bg-notr-100 text-notr-700 hover:bg-notr-200",
                  )}
                >
                  <span className="text-sm font-medium">{YONTEM_ADI[y]}</span>
                  {/*
                    BEDEL ETIKETIN ALTINDA, SECILDIKTEN SONRA DEGIL.
                    Kaybi kaybettikten sonra ogrenmek, ayni gun
                    `DegisiklikTalebi`de duzeltilen "geri donussuzluk
                    gonderdikten sonra soyleniyordu" hatasinin aynisi.

                    Renk tek basina anlam tasimiyor: "URETILMEZ" sozcugu
                    metnin kendisinde.
                  */}
                  <span
                    className={cn(
                      "text-xs",
                      secili
                        ? "text-notr-0/80"
                        : bedel.kayip
                          ? "text-uyari-600 font-medium"
                          : "text-notr-600",
                    )}
                  >
                    {bedel.metin}
                  </span>
                </button>
              );
            })}
          </div>

          {yontem === "toplu" && <TopluGiris deneme={secili} />}
          {yontem === "net" && <NetGirisi key={secili.id} deneme={secili} />}
          {yontem === "cevap" && <CevapGirisi deneme={secili} />}
        </>
      )}
    </div>
  );
}

// =====================================================================
// TOPLU TABLO — F-SYS-04
// =====================================================================

function TopluGiris({ deneme }: { deneme: DenemeSatiri }) {
  const [metin, setMetin] = useState("");
  const [onizleme, setOnizleme] = useState<TopluOnizleme | null>(null);
  const [sonuc, setSonuc] = useState<string | null>(null);

  const onizle = useMutation({
    mutationFn: () => topluOnizle(deneme.id, metin),
    onSuccess: (v) => {
      setOnizleme(v);
      setSonuc(null);
    },
  });

  const uygula = useMutation({
    mutationFn: () => topluUygula(deneme.id, metin),
    onSuccess: (v) => {
      const parcalar = [`${v.yazilan} sonuç yazıldı.`];
      /*
        HATALI VE BAŞARISIZ AYRI SAYILIYOR: biri "satır okunamadı",
        öteki "okundu ama yazılamadı". Tek sayıda toplasaydık,
        kullanıcı hangisini düzelteceğini bilemezdi.
      */
      if (v.hatali.length > 0) parcalar.push(`${v.hatali.length} satır okunamadı.`);
      if (v.basarisiz.length > 0) parcalar.push(`${v.basarisiz.length} satır yazılamadı.`);
      setSonuc(parcalar.join(" "));
      setOnizleme(null);
      setMetin("");
    },
  });

  const toplamSoru = deneme.bolumler.reduce((t, b) => t + b.soruSayisi, 0);

  return (
    <div className="flex flex-col gap-3">
      <Kart
        baslik="Toplu tablo"
        aciklama="Excel'den kopyalayıp yapıştırın. İLK SATIR BAŞLIK olmalı; başlıksız tablo kabul edilmiyor."
      >
        <div className="flex flex-col gap-3">
          {/*
            BİÇİMİ EKRAN YAZIYOR ÇÜNKÜ SUNUCU YAZMIYOR. İlk yazımda
            "her soru ayrı sütun" diye anlatmıştım — yanlıştı ve canlı
            denemede sunucu "Cevaplar sütunu bulunamadı" dedi.
            Ayrıştırıcı ÜÇ sütun arıyor: numara, cevapların TAMAMI tek
            hücrede, ve isteğe bağlı süre.
          */}
          <div className="rounded-kart bg-notr-50 text-notr-800 p-3 text-sm">
            <p className="font-medium">Beklenen biçim</p>
            <pre className="mt-1 overflow-x-auto font-mono text-xs">
              {`no;cevaplar;sure
90;${"ABCDE".repeat(Math.max(1, Math.ceil(toplamSoru / 5))).slice(0, toplamSoru)};110`}
            </pre>
            <p className="text-notr-600 mt-1">
              Cevapların tamamı <strong>tek hücrede</strong> ({toplamSoru} harf). Başlık olarak “no
              / numara / öğrencino”, “cevaplar / kodlama / işaretler” ve “süre / dakika” kabul
              edilir. Süre sütunu isteğe bağlı.
            </p>
          </div>
          <MetinAlani
            etiket="Yapıştırılan tablo"
            rows={8}
            daktilo
            value={metin}
            onChange={(e) => setMetin(e.target.value)}
            yardim="Ayraç kendiliğinden bulunur — Türkçe Excel noktalı virgül kullanır, sekme de olur."
          />

          {onizle.isError && (
            <p role="alert" className="text-hata-500 text-sm font-medium">
              {onizle.error instanceof ApiHatasi ? onizle.error.message : "Ön izleme alınamadı."}
            </p>
          )}
          {uygula.isError && (
            <p role="alert" className="text-hata-500 text-sm font-medium">
              {uygula.error instanceof ApiHatasi ? uygula.error.message : "Uygulanamadı."}
            </p>
          )}
          {sonuc && (
            <p role="status" className="rounded-kart bg-notr-50 text-notr-800 px-3 py-2 text-sm">
              {sonuc}
            </p>
          )}

          <div>
            <Buton
              gorunum="ikincil"
              yukleniyor={onizle.isPending}
              disabled={metin.trim().length === 0}
              onClick={() => onizle.mutate()}
            >
              Ön izle
            </Buton>
            <p className="text-notr-600 mt-1.5 text-xs">
              Ön izleme hiçbir şey yazmaz; ne olacağını gösterir.
            </p>
          </div>
        </div>
      </Kart>

      {onizleme && (
        <Kart
          baslik="Ön izleme"
          aciklama={`Ayraç: ${onizleme.ayirac}. ${onizleme.yuklenecek.length} satır yüklenecek, ${onizleme.hatali.length} satır okunamadı.`}
          ton={onizleme.hatali.length > 0 ? "uyari" : "notr"}
        >
          <div className="flex flex-col gap-3">
            {onizleme.yuklenecek.some((y) => y.uzerineYazilacak) && (
              /*
                ÜZERİNE YAZILACAKLAR ÖNDE VE AYRI. Sessizce üzerine
                yazmak, daha önce girilmiş bir sonucun kaybolması
                demek — ve bunu ancak sonucu arayan kişi fark eder.
              */
              <p className="rounded-kart shadow-kart border-uyari-500 bg-uyari-50 text-notr-800 border px-3 py-2 text-sm">
                <strong>
                  {onizleme.yuklenecek.filter((y) => y.uzerineYazilacak).length} öğrencinin ZATEN
                  sonucu var
                </strong>{" "}
                ve üzerine yazılacak. Aşağıdaki listede işaretli.
              </p>
            )}

            {onizleme.yuklenecek.length > 0 && (
              <Tablo
                satirlar={onizleme.yuklenecek}
                anahtar={(y) => `${y.satirNo}`}
                kartBasligi={1}
                sutunlar={[
                  { baslik: "Satır", sayisal: true, hucre: (y) => y.satirNo },
                  {
                    baslik: "Öğrenci",
                    mobil: true,
                    className: "font-medium",
                    hucre: (y) => y.ogrenciAdi,
                  },
                  { baslik: "No", mobil: true, sayisal: true, hucre: (y) => y.ogrenciNo },
                  {
                    baslik: "İşaretli",
                    mobil: true,
                    sayisal: true,
                    hucre: (y) => y.isaretliSayisi,
                  },
                  {
                    baslik: "Süre",
                    sayisal: true,
                    hucre: (y) => (y.sureDakika === null ? "—" : `${y.sureDakika} dk`),
                  },
                  {
                    baslik: "Durum",
                    mobil: true,
                    hucre: (y) =>
                      y.uzerineYazilacak ? (
                        <Rozet ton="uyari">Üzerine yazılacak</Rozet>
                      ) : (
                        <Rozet ton="onay">Yeni</Rozet>
                      ),
                  },
                ]}
              />
            )}

            {onizleme.hatali.length > 0 && (
              <div className="rounded-kart shadow-kart border-hata-500 bg-hata-500/5 border p-3">
                <p className="text-hata-500 text-sm font-medium">
                  Okunamayan satırlar — bunlar YAZILMAYACAK:
                </p>
                <ul className="text-notr-800 mt-1 flex flex-col gap-1 text-sm">
                  {onizleme.hatali.map((h) => (
                    <li key={h.satirNo}>
                      <span className="font-medium">Satır {h.satirNo}:</span> {h.sebep}
                      <span className="text-notr-600 block truncate font-mono text-xs">
                        {h.ham}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <Buton
                yukleniyor={uygula.isPending}
                disabled={onizleme.yuklenecek.length === 0}
                onClick={() => uygula.mutate()}
              >
                {onizleme.yuklenecek.length} sonucu uygula
              </Buton>
              <p className="text-notr-600 mt-1.5 text-xs">
                Metin yeniden çözülür. Bir satır patlarsa diğerleri devam eder — 29. satırdaki
                sorun, yazılmış 28 sonucu geri aldırmaz.
              </p>
            </div>
          </div>
        </Kart>
      )}
    </div>
  );
}

// =====================================================================
// DERS BAZLI NET
// =====================================================================

function NetGirisi({ deneme }: { deneme: DenemeSatiri }) {
  const [ogrenciId, setOgrenciId] = useState("");
  const [sure, setSure] = useState("");
  const [netler, setNetler] = useState<
    Record<string, { dogru: string; yanlis: string; bos: string }>
  >({});
  const [bilgi, setBilgi] = useState<string | null>(null);

  const ogrenciler = useQuery({ queryKey: ["ogrenciler"], queryFn: ogrenciListesi, retry: false });

  // F-EXM-24 — form açıkken sürüm kendiliğinden yenilenmez; eski D/Y/B yeni iptali onaylamaz.
  const hazirlik = useQuery({
    queryKey: ["ders-net-hazirlik", deneme.id, ogrenciId],
    queryFn: () =>
      api<{ iptalSurumu: string }>(`/deneme/${encodeURIComponent(deneme.id)}/toplu-net/hazirlik`, {
        sorgu: { ogrenciId },
      }),
    enabled: ogrenciId !== "",
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const gir = useMutation({
    mutationFn: () => {
      if (!hazirlik.data) throw new Error("Giriş hazırlığı tamamlanamadı.");
      const girdi: DersNetGirisi = {
        iptalSurumu: hazirlik.data.iptalSurumu,
        ogrenciId,
        netler: deneme.bolumler.map((b) => ({
          sectionId: b.id,
          dogru: Number(netler[b.id]?.dogru ?? 0),
          yanlis: Number(netler[b.id]?.yanlis ?? 0),
          bos: Number(netler[b.id]?.bos ?? 0),
        })),
        sureDakika: sure.trim() ? Number(sure) : null,
      };
      return topluNetGirisi(deneme.id, girdi);
    },
    onSuccess: () => {
      setBilgi("Sonuç kaydedildi.");
      setNetler({});
      setSure("");
    },
  });

  const alan = (bolumId: string, k: "dogru" | "yanlis" | "bos"): string =>
    netler[bolumId]?.[k] ?? "";

  const yaz = (bolumId: string, k: "dogru" | "yanlis" | "bos", v: string): void =>
    setNetler((n) => ({
      ...n,
      [bolumId]: { dogru: "", yanlis: "", bos: "", ...n[bolumId], [k]: v },
    }));

  /*
    TOPLAM SORU SAYISINI AŞAN GİRİŞ ENGELLENİYOR. Sunucu da her alanı
    tek tek sınırlıyor ama toplamı bilmiyor; 40 soruluk bölüme
    30+15+5 girilirse bu bir yazım hatası ve kullanıcıya SAYIYI
    göstererek söylemek, "geçersiz" demekten faydalı.
  */
  const tasan = deneme.bolumler.filter((b) => {
    const n = netler[b.id];
    if (!n) return false;
    const t = Number(n.dogru || 0) + Number(n.yanlis || 0) + Number(n.bos || 0);
    return t > b.soruSayisi;
  });

  const eksikBolum = deneme.bolumler.some((b) => {
    const n = netler[b.id];
    return !n || (n.dogru === "" && n.yanlis === "" && n.bos === "");
  });

  return (
    <Kart
      baslik="Ders bazlı net girişi"
      aciklama="Cevap cevap giriş yapılmaz; soru ve konu bazlı analiz ÜRETİLMEZ. Olmayan veriden çıkarım yapmamak için bilinçli."
    >
      <div className="flex flex-col gap-3">
        {ogrenciler.isPending ? (
          <Iskelet satir={1} />
        ) : (
          <Secim
            etiket="Öğrenci"
            value={ogrenciId}
            onChange={(e) => {
              setOgrenciId(e.target.value);
              setNetler({});
              setSure("");
              setBilgi(null);
              gir.reset();
            }}
            className="w-full sm:w-80"
          >
            <option value="">Seçin…</option>
            {(ogrenciler.data?.ogrenciler ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.ad} {o.soyad}
              </option>
            ))}
          </Secim>
        )}

        {hazirlik.isError && <p role="alert">Giriş hazırlığı alınamadı. Formu yeniden açın.</p>}

        {deneme.bolumler.map((b) => (
          <fieldset key={b.id} className="rounded-kart border-notr-200 shadow-kart border p-3">
            <legend className="text-notr-900 px-1 text-sm font-medium">
              {b.ad} · {b.soruSayisi} soru
            </legend>
            <div className="grid grid-cols-3 gap-2">
              <Alan
                etiket="Doğru"
                inputMode="numeric"
                value={alan(b.id, "dogru")}
                onChange={(e) => yaz(b.id, "dogru", e.target.value)}
              />
              <Alan
                etiket="Yanlış"
                inputMode="numeric"
                value={alan(b.id, "yanlis")}
                onChange={(e) => yaz(b.id, "yanlis", e.target.value)}
              />
              <Alan
                etiket="Boş"
                inputMode="numeric"
                value={alan(b.id, "bos")}
                onChange={(e) => yaz(b.id, "bos", e.target.value)}
              />
            </div>
          </fieldset>
        ))}

        <Alan
          etiket="Süre (dakika)"
          inputMode="numeric"
          value={sure}
          onChange={(e) => setSure(e.target.value)}
          yardim="İsteğe bağlı."
          className="w-40"
        />

        {tasan.length > 0 && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {tasan.map((b) => `${b.ad}: girilen toplam ${b.soruSayisi} soruyu aşıyor`).join(" · ")}
          </p>
        )}
        {gir.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {gir.error instanceof ApiHatasi ? gir.error.message : "Kaydedilemedi."}
          </p>
        )}
        {gir.error instanceof ApiHatasi && gir.error.kod === "IPTAL_SURUMU_DEGISTI" && (
          <Buton
            onClick={() => {
              // F-EXM-24 — yeni sürüme geçiş, iptal öncesi girilmiş sayıları yeniden onaylamamalı.
              setNetler({});
              setSure("");
              setBilgi(null);
              gir.reset();
              void hazirlik.refetch();
            }}
          >
            Güncel formu aç
          </Buton>
        )}
        {bilgi && (
          <p role="status" className="text-notr-700 text-sm">
            {bilgi}
          </p>
        )}

        <div>
          <Buton
            yukleniyor={gir.isPending}
            disabled={
              !hazirlik.data || hazirlik.isFetching || !ogrenciId || eksikBolum || tasan.length > 0
            }
            onClick={() => {
              setBilgi(null);
              gir.mutate();
            }}
          >
            Netleri kaydet
          </Buton>
        </div>
      </div>
    </Kart>
  );
}

// =====================================================================
// CEVAP CEVAP GİRİŞ
// =====================================================================

function CevapGirisi({ deneme }: { deneme: DenemeSatiri }) {
  const [ogrenciId, setOgrenciId] = useState("");
  const [sure, setSure] = useState("");
  const [cevaplar, setCevaplar] = useState<Record<string, string>>({});
  const [bilgi, setBilgi] = useState<string | null>(null);

  const ogrenciler = useQuery({ queryKey: ["ogrenciler"], queryFn: ogrenciListesi, retry: false });

  /*
    Soru numaraları bölümler boyunca ARDIŞIK: 1. bölüm 1–40, 2. bölüm
    41–60… Sunucu `cevaplar` sözlüğünü bu numaralarla bekliyor.
  */
  const sorular = useMemo(() => {
    const liste: Array<{ no: number; bolum: string }> = [];
    let sayac = 1;
    for (const b of deneme.bolumler) {
      for (let i = 0; i < b.soruSayisi; i++) liste.push({ no: sayac++, bolum: b.ad });
    }
    return liste;
  }, [deneme.bolumler]);

  const gir = useMutation({
    mutationFn: () =>
      uzmanSonucGirisi(deneme.id, {
        ogrenciId,
        cevaplar,
        sureDakika: sure.trim() ? Number(sure) : null,
      }),
    onSuccess: () => {
      setBilgi("Sonuç kaydedildi.");
      setCevaplar({});
      setSure("");
    },
  });

  const isaretli = Object.keys(cevaplar).length;

  return (
    <Kart
      baslik="Cevap cevap giriş"
      aciklama={`${sorular.length} soru. Boş bırakılan soru "cevaplanmadı" sayılır — sıfır puan değil, cevapsız.`}
    >
      <div className="flex flex-col gap-3">
        {ogrenciler.isPending ? (
          <Iskelet satir={1} />
        ) : (
          <Secim
            etiket="Öğrenci"
            value={ogrenciId}
            onChange={(e) => setOgrenciId(e.target.value)}
            className="w-full sm:w-80"
          >
            <option value="">Seçin…</option>
            {(ogrenciler.data?.ogrenciler ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.ad} {o.soyad}
              </option>
            ))}
          </Secim>
        )}

        <p className="text-notr-600 text-sm">
          {isaretli} / {sorular.length} işaretlendi
        </p>

        <ol className="flex flex-col gap-1">
          {sorular.map((s) => (
            <li key={s.no} className="flex flex-wrap items-center gap-2">
              <span className="text-notr-600 w-16 shrink-0 text-sm tabular-nums">{s.no}.</span>
              <div className="flex gap-1">
                {SECENEKLER.map((h) => {
                  const secili = cevaplar[String(s.no)] === h;
                  return (
                    <button
                      key={h}
                      type="button"
                      aria-label={`Soru ${s.no}, ${h} şıkkı`}
                      aria-pressed={secili}
                      onClick={() =>
                        setCevaplar((c) => {
                          const y = { ...c };
                          /* Aynı şıkka basmak seçimi KALDIRIYOR — yanlış
                             basılan bir şık düzeltilebilmeli. */
                          if (y[String(s.no)] === h) delete y[String(s.no)];
                          else y[String(s.no)] = h;
                          return y;
                        })
                      }
                      className={cn(
                        "min-h-dokunma min-w-dokunma rounded-alan text-sm font-medium",
                        "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                        secili
                          ? "bg-marka-600 text-notr-0"
                          : "bg-notr-100 text-notr-800 hover:bg-notr-200",
                      )}
                    >
                      {h}
                    </button>
                  );
                })}
              </div>
              <span className="text-notr-600 text-xs">{s.bolum}</span>
            </li>
          ))}
        </ol>

        <Alan
          etiket="Süre (dakika)"
          inputMode="numeric"
          value={sure}
          onChange={(e) => setSure(e.target.value)}
          yardim="İsteğe bağlı."
          className="w-40"
        />

        {gir.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {gir.error instanceof ApiHatasi ? gir.error.message : "Kaydedilemedi."}
          </p>
        )}
        {bilgi && (
          <p role="status" className="text-notr-700 text-sm">
            {bilgi}
          </p>
        )}

        <div>
          <Buton
            yukleniyor={gir.isPending}
            disabled={!ogrenciId || isaretli === 0}
            onClick={() => {
              setBilgi(null);
              gir.mutate();
            }}
          >
            Sonucu kaydet
          </Buton>
        </div>
      </div>
    </Kart>
  );
}
